import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Field, Text, TextInput, tokens, Icon } from '@hotjar/hotsauce';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { useEmailTrackingCode } from './useEmailTrackingCode';
import type { OnSubmitData } from './useEmailTrackingCode';

interface EmailTrackingCodeMobileProps {
  onSuccess: () => void;
}

const TrackingCodeHelp = {
  Field: styled(Field)`
    margin: ${tokens.spacer.x3} 0;

    label {
      text-transform: none;
    }
  `,
};

const StyledButton = styled(Button)`
  width: 100%;
  justify-content: center;
`;

export const EmailTrackingCodeMobile = ({
  onSuccess,
}: EmailTrackingCodeMobileProps): JSX.Element => {
  const { t } = useTranslation();
  const { onContinueToOverview, onSubmit, isProcessing, isEmailSent, lastRecipientEmail } =
    useEmailTrackingCode(true);
  const { register, handleSubmit, errors } = useForm<OnSubmitData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (isEmailSent) {
      onSuccess();
    }
  }, [isEmailSent, onSuccess]);

  return isEmailSent ? (
    <>
      <Text
        style={{ marginBottom: tokens.spacer.x3 }}
        as="p"
        data-testid="email-sent-message"
        textColor="text_success"
      >
        <Icon icon="check_alt" css={{ marginRight: tokens.spacer.x1 }} />
        <Trans
          i18nKey="activation:tracking-code.installation.send-email.email-sent"
          values={{ email: lastRecipientEmail }}
        >
          Email successfully sent to <b>{lastRecipientEmail}</b>.
        </Trans>
      </Text>

      <StyledButton kind="primary" onClick={onContinueToOverview}>
        {t(
          'activation:tracking-code.installation.send-email.continue-to-dashboard-overview-mobile',
        )}
      </StyledButton>
    </>
  ) : (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-testid="send-tracking-code-email-mobile"
    >
      <TrackingCodeHelp.Field
        css={{ width: '100%', margin: `0 0 ${tokens.spacer.x2} 0` }}
        inputId="recipientEmail"
        labelText={t('activation:tracking-code.installation.send-email.recipient-email-label')}
        invalidText={errors.recipientEmail?.message}
        isInvalid={!!errors.recipientEmail}
        isRequired
      >
        <TextInput
          id="recipientEmail"
          name="recipientEmail"
          autoFocus
          ref={
            register &&
            register({
              required: t(
                'activation:tracking-code.installation.send-email.recipient-email-errors.required',
              ) as string,
              validate: (value: string) =>
                validateEmail(value) ||
                (t(
                  'activation:tracking-code.installation.send-email.recipient-email-errors.invalid',
                ) as string),
            })
          }
          placeholder={t(
            'activation:tracking-code.installation.send-email.recipient-email-placeholder',
          )}
          isInvalid={!!errors.recipientEmail}
          isRequired
        />
      </TrackingCodeHelp.Field>
      <StyledButton
        kind="secondary"
        type="submit"
        isLoading={isProcessing}
        isDisabled={!!errors.recipientEmail}
        loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
      >
        {t('activation:tracking-code.installation.send-email.cta-mobile')}
      </StyledButton>
    </form>
  );
};
